import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, Bot, Rocket, Shield } from "lucide-react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";

export default function Pricing() {
  const { t } = useTranslation();

  return (
    <div className="container py-12 max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-extrabold tracking-tight lg:text-5xl mb-4 gradient-text">
          {t('pricing.title')}
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* FREE PLAN */}
        <Card className="flex flex-col border-border/50">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-500" />
              {t('pricing.standard.title')}
            </CardTitle>
            <CardDescription>{t('pricing.standard.description')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">{t('pricing.standard.price')}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <span className="font-medium">{t('pricing.standard.features.unlimited')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <span>{t('pricing.standard.features.search')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <span>{t('pricing.standard.features.chat')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <span>{t('pricing.standard.features.support')}</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button className="w-full" variant="outline" asChild>
              <Link href="/post">{t('pricing.standard.cta')}</Link>
            </Button>
          </CardFooter>
        </Card>

        {/* PROMOTED */}
        <Card className="flex flex-col border-purple-500/50 relative overflow-hidden bg-purple-500/5">
          <CardHeader>
            <CardTitle className="text-2xl text-purple-600 flex items-center gap-2">
              <Rocket className="w-6 h-6" />
              {t('pricing.boost.title')}
            </CardTitle>
            <CardDescription>{t('pricing.boost.description')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">{t('pricing.boost.price')}</span>
              <span className="text-muted-foreground text-sm"> {t('pricing.boost.period')}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-purple-600" />
                <span className="font-medium">{t('pricing.boost.features.top')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-purple-600" />
                <span>{t('pricing.boost.features.highlight')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="h-5 w-5 text-purple-600" />
                <span>{t('pricing.boost.features.gallery')}</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" asChild>
              <Link href="/dashboard">{t('pricing.boost.cta')}</Link>
            </Button>
          </CardFooter>
        </Card>

        {/* AI SALESMAN */}
        <Card className="flex flex-col bg-slate-950 text-white border-slate-800 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
            {t('pricing.agent.new')}
          </div>
          <CardHeader>
            <CardTitle className="text-2xl text-blue-400 flex items-center gap-2">
              <Bot className="w-6 h-6" />
              {t('pricing.agent.title')}
            </CardTitle>
            <CardDescription className="text-slate-400">{t('pricing.agent.description')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">{t('pricing.agent.price')}</span>
              <span className="text-muted-foreground text-slate-400 text-sm"> {t('pricing.agent.unit')}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            <ul className="space-y-4 text-sm text-slate-300">
              <li className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-1 rounded-full"><Bot className="h-4 w-4 text-blue-400" /></div>
                <span className="font-medium text-white">{t('pricing.agent.features.hunting')}</span>
                <p className="text-xs text-slate-500 pl-8 -mt-2">{t('pricing.agent.features.huntingDesc')}</p>
              </li>
              <li className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-1 rounded-full"><Check className="h-4 w-4 text-blue-400" /></div>
                <span>{t('pricing.agent.features.notifications')}</span>
                <p className="text-xs text-slate-500 pl-8 -mt-2">{t('pricing.agent.features.notificationsDesc')}</p>
              </li>
              <li className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-1 rounded-full"><Check className="h-4 w-4 text-blue-400" /></div>
                <span>{t('pricing.agent.features.advice')}</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" asChild>
              <Link href="/dashboard">{t('pricing.agent.cta')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>

      <div className="mt-16 bg-muted/30 p-8 rounded-xl text-center">
        <h3 className="text-lg font-semibold mb-2">{t('pricing.highVolume.title')}</h3>
        <p className="text-muted-foreground mb-4">{t('pricing.highVolume.description')}</p>
        <Button variant="link" asChild><Link href="/contact">{t('pricing.highVolume.cta')}</Link></Button>
      </div>
    </div>
  );
}
